<template>
  <section
    v-if="state.reviews.length"
    v-view-tracker="getParams()"
    class="reviews-slider"
  >
    <div class="reviews-slider__header">
      <h2 class="reviews-slider__title">{{ title }}</h2>
    </div>
    <UiSlider
      :slides="state.reviews"
      :slides-per-view="slidesPerView"
      :space-between="spaceBetween"
      :mousewheel="false"
      arrows-always-visible
    >
      <template #slide="sliderProps">
        <ReviewCard
          :key="(sliderProps.slide as unknown as ReviewsListItemSnack).id"
          v-bind="bindProps(sliderProps.slide as unknown as ReviewsListItemSnack)"
          @click="useCard(sliderProps.slide as ReviewsListItem)"
        />
      </template>

      <template #rightArrowIcon>
        <icon-base
          name="right-arrow"
          color="--alias-color-icon-subtle"
        />
      </template>
      <template #leftArrowIcon>
        <icon-base
          style="transform: scaleX(-1)"
          name="right-arrow"
          color="--alias-color-icon-subtle"
        />
      </template>
    </UiSlider>
  </section>
</template>

<script setup lang="ts">
  import { UiSlider } from '@experience/core-slider-ui';
  import { computed, onBeforeMount, reactive } from 'vue';

  import { trackEvent, useViewTracker } from '@/services/analytics';

  import { getReviewsList } from '@/shared/api/experience';
  import type { ReviewsListItem } from '@/shared/api/experience';
  import { choosePlural, useScreenSize } from '@/shared/lib';
  import { type ReviewsListItemSnack } from '@/shared/models';
  import { useCategoryStore } from '@/shared/store';
  import { IconBase } from '@/shared/ui/icon';
  import { camelize } from '@/shared/utils/camelize';
  import ReviewCard from './ReviewCard.vue';

  import { reviewCount, travelerCount } from '@/widgets/reviewsSlider';

  const categoryStore = useCategoryStore();

  const state: {
    maxReviewsCount: number;
    reviews: ReviewsListItem[];
  } = reactive({
    maxReviewsCount: 15,
    reviews: [],
  });

  const getParams = (params = {}) => {
    return {
      category_title: categoryStore.seoCategoryTitle,
      category_id: categoryStore.seoCategoryId,
      ...params,
    };
  };

  const bindProps = (sliderProps: ReviewsListItemSnack) => {
    return {
      rating: sliderProps.rating,
      experienceTitle: sliderProps.experience?.title,
      experienceUrl: sliderProps.experience?.url,
      reviewerName: sliderProps.name,
      reviewerAvatar: sliderProps.avatar.small ?? undefined,
      createdAt: sliderProps.createdAt,
      travelerLevel: sliderProps.travelerLevel,
      reviewText: sliderProps.text,
    };
  };

  const useCard = (exp: ReviewsListItem) => {
    trackEvent('category:reviews.click', getParams({ experience_id: exp.experience.id }));
  };

  const vViewTracker = useViewTracker('category:reviews.view');

  const { isMobile, isTablet, isDesktop } = useScreenSize();

  const reviewsCount = computed(() => state.reviews.length);
  const title = computed(() => {
    if (reviewsCount.value === 1) {
      return '1 отзыв путешественника';
    }

    return `${reviewsCount.value} ${choosePlural(reviewsCount.value, reviewCount.items_pluralized)} ${choosePlural(reviewsCount.value, travelerCount.items_pluralized)}`;
  });
  const slidesCountDesktop = computed(() => {
    if (isDesktop.value && (reviewsCount.value === 1 || reviewsCount.value === 2)) {
      return 2;
    }

    return 3;
  });
  const slidesCountTablet = computed(() => {
    if (isTablet.value && reviewsCount.value === 1) {
      return 1;
    }
    if (isTablet.value && reviewsCount.value === 2) {
      return 2;
    }

    return 2.2;
  });
  const slidesCountMobile = computed(() => {
    if (isMobile.value && reviewsCount.value === 1) {
      return 1;
    }

    return 1.2;
  });

  const slidesPerView = computed(() => {
    if (isMobile.value) {
      return slidesCountMobile.value;
    }
    if (isTablet.value) {
      return slidesCountTablet.value;
    }
    if (isDesktop.value) {
      return slidesCountDesktop.value;
    }

    return 1;
  });

  const spaceBetween = computed(() => {
    if (isTablet.value || isMobile.value) {
      return 16;
    }

    return 24;
  });

  const getReviews = async () => {
    try {
      const { data } = await getReviewsList({
        query: { ...categoryStore.reviewsApiFilters },
      });

      state.reviews = camelize(data?.results ?? []);
    } catch (e) {
      // ignore
      console.error(e);
    }
  };

  onBeforeMount(async () => {
    await getReviews();
  });
</script>

<style scoped lang="scss">
  @use '@experience/design-tokens/typography';
  @use '@experience/shared/dist/assets/adaptivity';

  .reviews-slider {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 24px;
    box-sizing: border-box;
    padding: var(--alias-space-40) 0;

    @include adaptivity.mobile {
      gap: 16px;
    }
  }

  .reviews-slider__header {
    display: flex;
    gap: 24px;
    align-items: end;
  }

  .reviews-slider__title {
    color: var(--alias-color-text-default);

    @include typography.base-800-750-extra;

    @include adaptivity.mobile {
      font-size: 20px;
      line-height: 24px;
    }
  }

  :deep(.btn-next) {
    right: 5px;
  }
</style>
